import React, { useEffect, useMemo, useState } from "react";
import { Check, Globe, DollarSign, Mail, Loader2, Eye, Send, Sparkles, Search, X, Camera, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { CURRENCY_META, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INDUSTRIES } from "@/lib/industries";

const DATE_OPTIONS = [
  { value: "mm/dd/yyyy" },
  { value: "dd/mm/yyyy" },
  { value: "yyyy-mm-dd" },
];

const CURRENCY_OPTIONS = [
  { code: "USD", name: "US Dollar" },
  { code: "CAD", name: "Canadian Dollar" },
];

function datePreview(fmt) {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  if (fmt === "dd/mm/yyyy") return `${dd}/${mm}/${yyyy}`;
  if (fmt === "yyyy-mm-dd") return `${yyyy}-${mm}-${dd}`;
  return `${mm}/${dd}/${yyyy}`;
}

function currencyPreview(code) {
  const meta = CURRENCY_META[code] || CURRENCY_META.USD;
  const n = 4200;
  return (
    meta.symbol +
    n.toLocaleString(meta.locale, {
      minimumFractionDigits: meta.decimals,
      maximumFractionDigits: meta.decimals,
    })
  );
}

export default function Settings() {
  const { user, updatePreferences } = useAuth();
  const [dateFmt, setDateFmt] = useState(user?.date_format || "mm/dd/yyyy");
  const [currency, setCurrencyState] = useState(user?.currency || "USD");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.date_format) setDateFmt(user.date_format);
    if (user?.currency) setCurrencyState(user.currency);
  }, [user?.date_format, user?.currency]);

  const save = async (patch, nextDateFmt, nextCurrency) => {
    setSaving(true);
    try {
      await updatePreferences(patch);
      if (nextDateFmt) setDateFmt(nextDateFmt);
      if (nextCurrency) setCurrencyState(nextCurrency);
      toast.success("Preferences updated");
    } catch (e) {
      toast.error("Could not update preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto space-y-8" data-testid="settings-page">
      <div>
        <div className="overline text-forest">Settings</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mt-1">
          Preferences
        </h1>
        <p className="text-ink-light mt-2 text-sm">
          Regional settings that apply everywhere in BizCashflow Pros — dashboard, transactions, reports, PDFs.
        </p>
      </div>

      {/* Date format */}
      <div className="card-surface p-6" data-testid="settings-date-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-forest/10 text-forest flex items-center justify-center shrink-0">
            <Globe className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div>
            <div className="overline mb-0.5">Regional</div>
            <div className="font-heading text-lg font-medium">Date format</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Date format">
          {DATE_OPTIONS.map((o) => {
            const active = dateFmt === o.value;
            return (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={saving}
                onClick={() => !active && save({ date_format: o.value }, o.value, null)}
                className={`text-left rounded-lg border px-4 py-3 transition-colors ${
                  active ? "border-forest bg-forest/5" : "border-border hover:border-ink-lighter"
                } ${saving ? "opacity-70" : ""}`}
                data-testid={`date-format-option-${o.value}`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-medium font-mono">{o.value}</span>
                  {active && <Check className="w-4 h-4 text-forest" strokeWidth={2} />}
                </div>
                <div className="text-xs text-ink-lighter num">Example: {datePreview(o.value)}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Currency */}
      <div className="card-surface p-6" data-testid="settings-currency-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-forest/10 text-forest flex items-center justify-center shrink-0">
            <DollarSign className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div>
            <div className="overline mb-0.5">Regional</div>
            <div className="font-heading text-lg font-medium">Currency</div>
            <p className="text-sm text-ink-light mt-1">
              We&apos;ll display all amounts using this currency. Existing transaction values aren&apos;t converted — they&apos;re just displayed with a new symbol.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Currency">
          {CURRENCY_OPTIONS.map((o) => {
            const active = currency === o.code;
            return (
              <button
                key={o.code}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={saving}
                onClick={() => !active && save({ currency: o.code }, null, o.code)}
                className={`text-left rounded-lg border px-4 py-3 transition-colors ${
                  active ? "border-forest bg-forest/5" : "border-border hover:border-ink-lighter"
                } ${saving ? "opacity-70" : ""}`}
                data-testid={`currency-option-${o.code}`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-medium">
                    <span className="font-mono mr-1.5">{o.code}</span>
                    <span className="text-ink-light">{o.name}</span>
                  </span>
                  {active && <Check className="w-4 h-4 text-forest" strokeWidth={2} />}
                </div>
                <div className="text-xs text-ink-lighter num">Example: {currencyPreview(o.code)}</div>
              </button>
            );
          })}
        </div>
      </div>

      <SpecialtiesCard />

      <BrandingCard />

      <DigestCard />
    </div>
  );
}

function BrandingCard() {
  const { user, refreshUser } = useAuth();
  const isAccountant = user && (user.role === "user" || user.role === "admin");
  const avatarInput = React.useRef();
  const logoInput = React.useRef();
  const [busy, setBusy] = React.useState(false);

  const doUpload = async (endpoint, file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded.");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (endpoint) => {
    setBusy(true);
    try {
      await api.delete(endpoint);
      toast.success("Removed.");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-surface p-6" data-testid="settings-branding-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-forest/10 text-forest flex items-center justify-center shrink-0">
          <Camera className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div>
          <div className="overline mb-0.5">Branding</div>
          <div className="font-heading text-lg font-medium">
            {isAccountant ? "Avatar & firm logo" : "Profile picture"}
          </div>
          <p className="text-sm text-ink-light mt-1">PNG, JPEG or WEBP · max 2 MB.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <UploadSlot
          label="Your avatar"
          testid="avatar"
          currentUrl={user?.avatar_url}
          placeholder={<Camera className="w-6 h-6 text-ink-lighter" strokeWidth={1.5} />}
          onPick={() => avatarInput.current?.click()}
          onRemove={() => doDelete("/auth/avatar")}
          busy={busy}
        />
        <input
          ref={avatarInput}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && doUpload("/auth/avatar", e.target.files[0])}
          data-testid="avatar-file-input"
        />

        {isAccountant && (
          <>
            <UploadSlot
              label="Firm logo"
              testid="firm-logo"
              currentUrl={user?.firm_logo_url}
              placeholder={<Building2 className="w-6 h-6 text-ink-lighter" strokeWidth={1.5} />}
              onPick={() => logoInput.current?.click()}
              onRemove={() => doDelete("/auth/firm-logo")}
              busy={busy}
            />
            <input
              ref={logoInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && doUpload("/auth/firm-logo", e.target.files[0])}
              data-testid="firm-logo-file-input"
            />
          </>
        )}
      </div>
    </div>
  );
}

function UploadSlot({ label, testid, currentUrl, placeholder, onPick, onRemove, busy }) {
  return (
    <div className="flex items-center gap-4" data-testid={`upload-slot-${testid}`}>
      <div className="w-20 h-20 rounded-xl bg-paper border border-border overflow-hidden flex items-center justify-center shrink-0">
        {currentUrl ? (
          <img src={currentUrl} alt={label} className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
        ) : placeholder}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={onPick}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-md border border-border hover:border-forest hover:text-forest disabled:opacity-60"
            data-testid={`${testid}-upload-btn`}
          >
            {busy ? "Uploading…" : currentUrl ? "Replace" : "Upload"}
          </button>
          {currentUrl && (
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              className="text-xs px-2 py-1.5 rounded-md text-ink-lighter hover:text-destructive"
              data-testid={`${testid}-remove-btn`}
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const MAX_SPECIALTIES = 5;

function SpecialtiesCard() {
  const { user, updatePreferences } = useAuth();
  const [selected, setSelected] = useState(user?.industry_specialties || []);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (Array.isArray(user?.industry_specialties)) {
      setSelected(user.industry_specialties);
    }
  }, [user?.industry_specialties]);

  // Accountants + super-admins can set specialties (super-admin acts as a Platform accountant)
  const canEdit = user && (user.role === "user" || user.role === "admin");

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return INDUSTRIES;
    return INDUSTRIES.filter((i) => i.toLowerCase().includes(t));
  }, [q]);

  const persist = async (next) => {
    setSaving(true);
    const prev = selected;
    setSelected(next);
    try {
      await updatePreferences({ industry_specialties: next });
    } catch (e) {
      setSelected(prev);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update specialties");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (industry) => {
    if (saving) return;
    if (selected.includes(industry)) {
      persist(selected.filter((s) => s !== industry));
    } else {
      if (selected.length >= MAX_SPECIALTIES) {
        toast.error(`You can pick at most ${MAX_SPECIALTIES} specialties. Remove one first.`);
        return;
      }
      persist([...selected, industry]);
    }
  };

  const clearAll = () => {
    if (saving || selected.length === 0) return;
    persist([]);
  };

  if (!canEdit) return null;

  return (
    <div className="card-surface p-6" data-testid="settings-specialties-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-forest/10 text-forest flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <div className="overline mb-0.5">Firm profile</div>
          <div className="font-heading text-lg font-medium">Industry specialties</div>
          <p className="text-sm text-ink-light mt-1">
            Tag up to {MAX_SPECIALTIES} industries your firm focuses on. The AI matcher heavily prefers accountants whose declared specialties match an incoming self-signup client&apos;s industry.
          </p>
        </div>
      </div>

      {/* Selected chips */}
      <div className="border-t border-border pt-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-ink-light">
            <span className="font-medium text-ink" data-testid="specialties-count">
              {selected.length} / {MAX_SPECIALTIES}
            </span>{" "}
            selected
          </div>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              disabled={saving}
              className="text-xs text-ink-lighter hover:text-ink underline decoration-dotted"
              data-testid="specialties-clear"
            >
              Clear all
            </button>
          )}
        </div>
        {selected.length === 0 ? (
          <div className="text-xs text-ink-lighter italic">No specialties selected yet.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="specialties-selected-list">
            {selected.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-forest/10 text-forest text-xs font-medium border border-forest/20"
                data-testid={`specialty-chip-${s}`}
              >
                {s}
                <button
                  type="button"
                  onClick={() => toggle(s)}
                  disabled={saving}
                  className="w-4 h-4 rounded-full hover:bg-forest/20 inline-flex items-center justify-center"
                  aria-label={`Remove ${s}`}
                  data-testid={`specialty-remove-${s}`}
                >
                  <X className="w-3 h-3" strokeWidth={2} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Search + grid of all industries */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-ink-lighter absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search industries…"
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-white text-sm focus:outline-none focus:border-forest"
          data-testid="specialties-search"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-72 overflow-y-auto pr-1" data-testid="specialties-options">
        {filtered.map((industry) => {
          const active = selected.includes(industry);
          const disabled = saving || (!active && selected.length >= MAX_SPECIALTIES);
          return (
            <button
              key={industry}
              type="button"
              onClick={() => toggle(industry)}
              disabled={disabled}
              className={`text-left rounded-lg border px-3 py-2 text-xs transition-colors flex items-start justify-between gap-2 ${
                active
                  ? "border-forest bg-forest/5 text-ink"
                  : "border-border hover:border-ink-lighter text-ink-light"
              } ${disabled && !active ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid={`specialty-option-${industry}`}
            >
              <span className="leading-tight">{industry}</span>
              {active && <Check className="w-3.5 h-3.5 text-forest shrink-0 mt-0.5" strokeWidth={2} />}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-xs text-ink-lighter italic py-4 text-center">
            No industries match &ldquo;{q}&rdquo;.
          </div>
        )}
      </div>
    </div>
  );
}

function DigestCard() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/digest/preferences");
      setPrefs(data);
    } catch {
      setPrefs({ weekly_digest_enabled: false, resend_configured: false });
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/digest/preferences", {
        weekly_digest_enabled: !prefs.weekly_digest_enabled,
      });
      setPrefs({ ...prefs, weekly_digest_enabled: data.weekly_digest_enabled });
      toast.success(data.weekly_digest_enabled ? "Weekly digest enabled" : "Weekly digest disabled");
    } catch (e) {
      toast.error("Could not update");
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/digest/preview");
      setPreviewHtml(data.html);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/digest/send-test");
      toast.success(`Sent to ${data.sent_to}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (!prefs) return null;
  const enabled = prefs.weekly_digest_enabled;

  return (
    <div className="card-surface p-6" data-testid="settings-digest-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-forest/10 text-forest flex items-center justify-center shrink-0">
          <Mail className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <div className="overline mb-0.5">Notifications</div>
          <div className="font-heading text-lg font-medium">Weekly digest</div>
          <p className="text-sm text-ink-light mt-1">
            Every Monday around 08:00 UTC we&apos;ll email you a summary of overdue tasks, work due this week, uploads waiting for review, and AI-picked next-actions.
          </p>
        </div>
      </div>

      {!prefs.resend_configured && (
        <div className="mb-4 p-3 rounded-lg bg-ochre/10 border border-ochre/30 text-xs text-ochre" data-testid="digest-resend-warning">
          Emails are disabled until <code className="font-mono">RESEND_API_KEY</code> is added to <code className="font-mono">backend/.env</code>. Everything else works — you can still preview.
        </div>
      )}

      <div className="flex items-center justify-between border-t border-border pt-4 mb-4">
        <div>
          <div className="text-sm font-medium">Enabled</div>
          <div className="text-xs text-ink-lighter">
            {prefs.last_digest_sent_at
              ? `Last sent: ${prefs.last_digest_sent_at.slice(0, 16).replace("T", " ")}`
              : "Never sent"}
          </div>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={saving}
          role="switch"
          aria-checked={enabled}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? "bg-forest" : "bg-border"} ${saving ? "opacity-60" : ""}`}
          data-testid="digest-toggle"
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`}
          />
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={preview}
          disabled={previewing}
          className="btn-secondary text-sm inline-flex items-center gap-1.5"
          data-testid="digest-preview-btn"
        >
          {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
          Preview
        </button>
        <button
          type="button"
          onClick={sendTest}
          disabled={sending || !prefs.resend_configured}
          className="btn-forest text-sm inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="digest-send-test-btn"
          title={!prefs.resend_configured ? "RESEND_API_KEY not configured" : "Send a real test email now"}
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Send test email
        </button>
      </div>

      {previewHtml && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm"
          onClick={() => setPreviewHtml(null)}
          data-testid="digest-preview-modal"
        >
          <div
            className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 flex items-center justify-between border-b border-border">
              <div className="font-heading text-lg font-medium">Weekly digest preview</div>
              <button onClick={() => setPreviewHtml(null)} className="text-ink-lighter hover:text-ink" data-testid="digest-preview-close">
                Close
              </button>
            </div>
            <iframe
              title="Digest preview"
              srcDoc={previewHtml}
              className="flex-1 w-full border-0"
              data-testid="digest-preview-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
